import { isPartialDate, nowPartialDate, partialDateToValue, sortPartialDate } from 'neuro-utils';
import { path } from 'Utility/ramdaReplacement';
import type { RelevantDocumentType, TreatmentDocumentType } from '../Document/definitions';
import { treatmentDocTypes } from '../Document/definitions';
import type { IFormContext } from 'Containers/FormContextHandler';
import type { IReadonlyDocument } from 'Store/readonly/readonlyGlobals';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';

export const patientHasSleepApneaDiagnosis = (documents: IDocumentWithTypeV2<'diagnosis'>[]): boolean =>
  filterByDocumentType(documents, 'diagnosis').some(
    d => d.diagnosis === 'G47.3' || d.diagnosis === 'sleepApneaSuspicion',
  );

export const patientHasRespiratoryFailureDiagnosis = (documents: IDocumentWithTypeV2<'diagnosis'>[]): boolean =>
  filterByDocumentType(documents, 'diagnosis').some(
    d => d.diagnosis === 'J96.1' || d.diagnosis === 'J96.9' || d.diagnosis === 'respiratoryFailureSuspicion',
  );

export const patientHasHadOxygenTherapy = (documents: IDocumentV2[]): boolean =>
  filterByDocumentType(documents, 'oxygenTherapy').length > 0;

export const patientHasHadHFNCTherapy = (documents: IDocumentV2[]): boolean =>
  filterByDocumentType(documents, 'hfncTherapy').length > 0;

export const getRightDocumentsForPlatform = (
  documents: IDocumentWithTypeV2<RelevantDocumentType>[],
): IDocumentWithTypeV2<TreatmentDocumentType>[] => {
  const dgDocs = filterByDocumentType(documents, 'diagnosis');
  const allSRPlatformDiagnosis = new Set([
    'G47.3',
    'J96.1',
    'J96.9',
    'sleepApneaSuspicion',
    'respiratoryFailureSuspicion',
  ]);

  const oxygenTherapyDocs = dgDocs.some(d => d.diagnosis === 'J96.1' || d.diagnosis === 'J96.9')
    ? filterByDocumentType(documents, 'oxygenTherapy')
    : dgDocs.every(d => !allSRPlatformDiagnosis.has(d.diagnosis ?? ''))
      ? filterByDocumentType(documents, 'oxygenTherapy')
      : [];
  const hfncTherapyDocs = dgDocs.some(d => d.diagnosis === 'J96.1' || d.diagnosis === 'J96.9')
    ? filterByDocumentType(documents, 'hfncTherapy')
    : dgDocs.every(d => !allSRPlatformDiagnosis.has(d.diagnosis ?? ''))
      ? filterByDocumentType(documents, 'hfncTherapy')
      : [];
  const papTherapyDocs = filterByDocumentType(documents, 'papTherapy');
  const madTherapyDocs = dgDocs.some(d => d.diagnosis === 'G47.3')
    ? filterByDocumentType(documents, 'madTherapy')
    : dgDocs.every(d => !allSRPlatformDiagnosis.has(d.diagnosis ?? ''))
      ? filterByDocumentType(documents, 'madTherapy')
      : [];
  const surgicalTreatmentDocs = filterByDocumentType(documents, 'sleepApneaSurgicalTreatment');
  const otherTreatmentDocs = filterByDocumentType(documents, 'otherTreatment');

  const therapyRefusalDocs = filterByDocumentType(documents, 'patientDoesNotWantRespiratorySupportTherapy');

  const treatmentDocs = [
    ...oxygenTherapyDocs,
    ...hfncTherapyDocs,
    ...papTherapyDocs,
    ...madTherapyDocs,
    ...surgicalTreatmentDocs,
    ...otherTreatmentDocs,
    ...therapyRefusalDocs,
  ];

  return treatmentDocs;
};

// Find out if the treatment has ended (has an endDate which is not in the future) and has been marked as ended
export const isEnded = (hasEnded: boolean[] | null | undefined, endDate: PartialDate | null | undefined): boolean => {
  if (hasEnded?.[0] === true && isPartialDate(endDate)) {
    const nowDate = partialDateToValue(nowPartialDate());
    const endedDate = partialDateToValue(endDate);
    if (endedDate <= nowDate) return true;
    else return false;
  } else return false;
};

/** Type-preserving filter: callers get back the same document type they passed in */
export const getActiveTreatments = <T extends IDocumentV2>(documents: T[]): T[] => {
  return documents.filter(d => {
    // patientDoesNotWantRespiratorySupportTherapy
    if (d._type === 'patientDoesNotWantRespiratorySupportTherapy') {
      return !isEnded([isPartialDate(path(['endDate'], d))], path(['endDate'], d));
    }
    // surgicalTreatment, otherTreatment
    if (
      !['surgicalTreatment', 'otherTreatment'].includes(d._type) ||
      (d._type === 'otherTreatment' &&
        !['referralToNutritionist', 'referralToWeightControl', 'psychiatristConsultation'].includes(
          path(['otherTreatmentType'], d) ?? '',
        ))
    ) {
      return !isEnded(path(['hasEnded'], d), path(['endDate'], d));
    }
    // others
    const date = path(['date'], d);
    return isPartialDate(date) && sortPartialDate(date, nowPartialDate()) <= 0;
  });
};

/** Type-preserving filter: callers get back the same document type they passed in */
export const getEndedTreatments = <T extends IDocumentV2>(documentArray: T[]): T[] =>
  documentArray.filter(t =>
    t._type === 'patientDoesNotWantRespiratorySupportTherapy'
      ? isEnded([isPartialDate(path(['endDate'], t))], path(['endDate'], t))
      : isEnded(path(['hasEnded'], t), path(['endDate'], t)),
  );

export const docType = (
  documents: IDocumentWithTypeV2<RelevantDocumentType>[],
  editing: string | undefined,
): TDocumentType | undefined => documents.find(d => d._id === editing)?._type;

export const getHeaderId = (
  documents: IDocumentWithTypeV2<RelevantDocumentType>[],
  editing: IFormContext['editing'],
  viewing: NonNullable<IFormContext['view']>['viewing'] | undefined,
): string => {
  const _type = docType(documents, editing ?? viewing);
  let type: string | undefined = _type;
  if (type === 'sleepApneaSurgicalTreatment') type = 'surgicalTreatment';

  return _type && (treatmentDocTypes as string[]).includes(_type) ? `treatment.${type}.title` : 'treatment.title';
};

/**
 * Function for checking if the current doc being edited is the first treatment doc
 * @param docs array of documents
 * @param view view-prop
 * @param editing editing-prop
 * @returns true if the current doc being edited is the first treatment doc, otherwise false
 */
export const checkIfCurrentDocumentIsTheFirstTreatment = (
  docs: IDocumentWithTypeV2<TreatmentDocumentType>[],
  view: IView | undefined,
  editing: string | undefined,
): boolean => {
  const treatmentDocs = filterDocumentsByType(docs, treatmentDocTypes)
    .sort((n1, n2) => n1._cdate - n2._cdate)
    .sort((n1, n2) => sortPartialDate(n1.date, n2.date));
  // If only one document in the array of documents, return true
  if (treatmentDocs.length === 1) return true;
  // If first of the docs is same as the one being edited, return true
  if (treatmentDocs[0] && treatmentDocs[0]._id === editing && isPartialDate(treatmentDocs[0].date)) return true;
  // If first of the docs is same as the one being viewed, return true
  if (treatmentDocs[0] && treatmentDocs[0]._id === view?.viewing && isPartialDate(treatmentDocs[0].date)) return true;
  return false;
};

/**
 * Function for checking if the type of sleepApneaFirstVisit or respiratoryFirstVisit document is acute treatment start
 * @param {IDocumentV2[]} docs array of documents
 * @param {'sleepApneaFirstVisit' | 'respiratoryFirstVisit' | undefined} type for specifying if the checked document is for sleep apnea or respiratory failure - if undefined, sleep apnea is used as default
 * @returns {boolean} true if the type of the first visit document is acute treatment start, false if it is referral
 */
export const checkIfAcuteTreatmentStart = (
  docs: IDocumentV2[],
  type?: SafeExtract<TDocumentType, 'sleepApneaFirstVisit' | 'respiratoryFirstVisit'>,
): boolean => {
  const firstVisitDoc = filterDocumentsByType(
    docs,
    type ? [type] : ['sleepApneaFirstVisit', 'respiratoryFirstVisit'],
  ).at(0);
  if (firstVisitDoc?.referralOrAcute === 'acute') return true;
  return false;
};

/**
 * Function for getting the date of first visit document if its type is referral
 * @param {IDocumentV2[]} docs array of documents
 * @param {'sleepApneaFirstVisit' | 'respiratoryFirstVisit' | undefined} type for specifying if the checked document is for sleep apnea or respiratory failure - if undefined, sleep apnea is used as default
 * @returns {PartialDate | null | undefined} date of first visit document if it exists and its type is referral, otherwise undefined
 */
export const getDateOfReferral = (
  docs: IDocumentV2[],
  type?: SafeExtract<TDocumentType, 'sleepApneaFirstVisit' | 'respiratoryFirstVisit'>,
): PartialDate | null | undefined => {
  const firstVisitDoc = filterDocumentsByType(docs, [type ?? 'sleepApneaFirstVisit']).at(0);
  return firstVisitDoc?.referralOrAcute === 'referral' ? firstVisitDoc.dateOfReferral : undefined;
};

/**
 * Function for checking if delay from suspicion to treatment start should be shown
 * @param acute tells if treatment has been started as acute
 * @param isFirstDoc tells if the current doc being edited is first of a kind
 * @returns boolean that indicates if delay should be shown or not
 */
export const showDelay = (acute: boolean, isFirstDoc: boolean): boolean => {
  // Do not show the question if acute treatment start
  if (acute) return false;
  // Do not show the question if the doc being edited is not first of a kind
  if (!isFirstDoc) return false;
  return true;
};

/**
 * Function for mapping treatment start/decision dates to PDNWRST endDates
 * If a treatment start/decision date greater than or equal to PDNWRST date is found, it is mapped to PDNWRST endDate
 * Else nothing is done (return empty array)
 * @param {IDocumentWithTypeV2<RelevantDocumentType>[]} documents array of treatment documents (including PDNWRST)
 * @returns {IDocumentWithTypeV2<'patientDoesNotWantRespiratorySupportTherapy'>[]} array of PDNWRST documents with mapped endDates OR empty array
 */
export const mapPatientDoesNotWantRespiratorySupportTherapyEndDates = (
  documents: IDocumentWithTypeV2<RelevantDocumentType>[],
): IDocumentWithTypeV2<'patientDoesNotWantRespiratorySupportTherapy'>[] => {
  const docs = structuredClone(documents);

  const treatmentDocs = filterDocumentsByType(docs, [
    'oxygenTherapy',
    'hfncTherapy',
    'papTherapy',
    'madTherapy',
    'sleepApneaSurgicalTreatment',
  ]).filter(d => d._editing === false);

  const pDocs = filterDocumentsByType(docs, ['patientDoesNotWantRespiratorySupportTherapy']).filter(
    d => d._editing === false,
  );

  // Find PDNWRST documents without endDates
  const pDocsWithoutEndDates = pDocs.filter(p => !isPartialDate(p.endDate));

  // const docTypesWithTreatmentDecision = ['papTherapy', 'madTherapy', 'surgicalTreatment'] satisfies TDocumentType[];

  if (treatmentDocs.length > 0 && pDocsWithoutEndDates.length > 0) {
    // Find, list and sort dates of treatment starts and/or decisions
    const treatmentDates = treatmentDocs
      .filter(d => {
        if ('treatmentDecisionDate' in d && isPartialDate(d.treatmentDecisionDate)) return true;
        return isPartialDate(d.date);
      })
      // Prefer treatment decision date since it should be earlier than start date
      .map(d =>
        'treatmentDecisionDate' in d && isPartialDate(d.treatmentDecisionDate) ? d.treatmentDecisionDate : d.date,
      )
      .sort((a, b) => sortPartialDate(a, b));

    // Map suitable endDates to PDNWRST documents
    return pDocsWithoutEndDates
      .map(p => {
        p.endDate = treatmentDates.find(date => sortPartialDate(date, p.date) >= 0);
        return p;
      })
      .filter(p => isPartialDate(p.endDate));
  }
  return [];
};

/**
 * Continuous treatment: has a start date and a possible end date
 * Treatments that are not continuous: surgicalTreatment and other treatments of type referralToNutritionist, referralToWeightControl and psychiatristConsultation
 * @param {IDocumentWithTypeV2<TreatmentDocumentType>} d treatment document
 * @returns {boolean} true if treatment is continuous, false if not
 */
export const isContinuous = (d: IDocumentWithTypeV2<TreatmentDocumentType>) =>
  ['oxygenTherapy', 'hfncTherapy', 'papTherapy', 'madTherapy', 'patientDoesNotWantRespiratorySupportTherapy'].includes(
    d._type,
  ) ||
  (d._type === 'otherTreatment' &&
    [undefined, 'postureTreatment', 'otherTreatment'].includes(path(['otherTreatmentType'], d) ?? ''));

/**
 * Function for categorizing treatments of given type by their status: active, ended, future or not started
 * @param {IDocumentWithTypeV2<T>[]} documents array of all documents in treatment section
 * @param {T} type document type to be checked
 * @returns {{ active: IDocumentWithTypeV2<T>[]; ended: IDocumentWithTypeV2<T>[]; future: IDocumentWithTypeV2<T>[]; notStarted: IDocumentWithTypeV2<T>[] }} object that has treatments categorized by their status
 */
export const categorizeTreatmentsByStatus = <T extends TreatmentDocumentType>(
  documents: IDocumentWithTypeV2<T>[],
  type: T,
): {
  active: IDocumentWithTypeV2<T>[];
  ended: IDocumentWithTypeV2<T>[];
  future: IDocumentWithTypeV2<T>[];
  notStarted: IDocumentWithTypeV2<T>[];
} => {
  const isStarted = (d: IDocumentWithTypeV2<T>) => (d._type === 'madTherapy' ? d.isTreatmentStarted === true : true);

  // Continuous treatments are active if they have no end date or it is in the future, non-continuous are active if their date is current date
  const activeDocs = documents
    .filter(d =>
      isContinuous(d)
        ? !isEnded(path(['hasEnded'], d), path(['endDate'], d)) &&
          (sortPartialDate(d.date, nowPartialDate()) <= 0 || type === 'patientDoesNotWantRespiratorySupportTherapy') &&
          isStarted(d)
        : sortPartialDate(d.date, nowPartialDate()) === 0,
    )
    .sort((n1, n2) => sortPartialDate(n1.date, n2.date));

  // Continuous treatments have ended if they have an end date and it has passed, non-continuous have ended if their date has passed
  const endedDocs = documents
    .filter(d =>
      isContinuous(d)
        ? isEnded(path(['hasEnded'], d), path(['endDate'], d)) &&
          (sortPartialDate(d.date, nowPartialDate()) <= 0 || type === 'patientDoesNotWantRespiratorySupportTherapy') &&
          isStarted(d)
        : sortPartialDate(d.date, nowPartialDate()) < 0,
    )
    .sort((n1, n2) => sortPartialDate(n1.date, n2.date));

  // Treatments are in the future if their start date is later than current date (PDNWRST excluded)
  const futureDocs =
    type !== 'patientDoesNotWantRespiratorySupportTherapy'
      ? documents
          .filter(d => sortPartialDate(d.date, nowPartialDate()) > 0)
          .sort((n1, n2) => sortPartialDate(n1.date, n2.date))
      : [];

  // For MAD therapy, treatments not started concern treatments marked as not started by radio selection
  const notStartedDocs =
    type === 'madTherapy'
      ? documents.filter(d => !isStarted(d)).sort((n1, n2) => sortPartialDate(n1.date, n2.date))
      : [];

  return {
    active: activeDocs,
    ended: endedDocs,
    future: futureDocs,
    notStarted: notStartedDocs,
  };
};

/**
 * Checks that provided readonly document is in the period between provided papTherapy document's date and possible endDate
 * @param {IReadonlyDocument} readonlyDocument - Readonly document to be checked
 * @param {Pick<IDocumentWithTypeV2<'papTherapy'>, '_type' | 'date' | 'hasEnded' | 'endDate'>} papTherapyDocument - Document used for date comparison (should be papTherapy, goes through validation)
 * @returns {boolean} - True if provided comparison document is papTherapy and readonly document is in papTherapy period
 */
export const readonlyDocumentIsInPapTherapyPeriod = (
  readonlyDocument: IReadonlyDocument,
  papTherapyDocument: Pick<IDocumentWithTypeV2<'papTherapy'>, '_type' | 'date' | 'hasEnded' | 'endDate'>,
): boolean => {
  return Boolean(
    papTherapyDocument._type === 'papTherapy' &&
    readonlyDocument.date &&
    sortPartialDate(readonlyDocument.date, papTherapyDocument.date) >= 0 &&
    (!papTherapyDocument.hasEnded ||
      !papTherapyDocument.endDate ||
      sortPartialDate(readonlyDocument.date, papTherapyDocument.endDate) < 0),
  );
};

/**
 * Gets latest ResMed device from an array of readonly documents
 * @param {IReadonlyDocument[]} readonlyDocuments - Readonly documents that should include latest ResMed device
 * @param {IDocumentWithTypeV2<'papTherapy'>} d - Document used to find correct time period for latest ResMed device (should be papTherapy, goes through validation)
 * @returns {IPapDevice | undefined} - Returns the ResMed device as mapped IPapDevice if found, undefined if not
 */
export const getLatestResMedDevice = (
  readonlyDocuments: IReadonlyDocument[],
  d: IDocumentWithTypeV2<'papTherapy'>,
): IPapDevice | undefined => {
  const readonlyDocumentsFiltered = readonlyDocuments.filter(
    readonlyDoc => readonlyDoc._type === 'resMedDevice' && readonlyDocumentIsInPapTherapyPeriod(readonlyDoc, d),
  );

  const resMedDevice = readonlyDocumentsFiltered.filter(d => d._type === 'resMedDevice').at(0);

  return resMedDevice ? { ...resMedDevice, id: '' } : undefined;
};
